<template>

  <div class="col-sm-12 system-parameter-form-container">

    <div class="card">

      <!-- LOADING -->
      <div
        v-if="loading"
        class="empty-state"
      >
        <i class="fas fa-spinner fa-spin"></i>

        <p>
          Loading...
        </p>
      </div>


      <!-- FORM -->
      <div
        v-else
        class="form-sections"
      >

        <!-- SYSTEM PARAMETER DETAILS -->
        <div class="info-section">

          <h5 class="section-title">
            System Parameter Details
          </h5>


          <div class="form-grid">

            <!-- PARAMETER NAME -->
            <div class="form-item">

              <label for="parameterName">
                Parameter Name

                <span class="req">
                  *
                </span>
              </label>


              <input
                id="parameterName"
                v-model.trim="parameterName"
                type="text"
                placeholder="Enter Parameter Name"
                maxlength="200"
                :disabled="isEditMode || saving"
                required
              />


              <small
                v-if="isEditMode"
                class="field-note"
              >
                Parameter name cannot be changed when editing.
              </small>

            </div>


            <!-- PARAMETER VALUE -->
            <div class="form-item">

              <label for="parameterValue">
                Parameter Value
              </label>


              <input
                id="parameterValue"
                v-model="parameterValue"
                type="text"
                placeholder="Enter Parameter Value"
                maxlength="200"
                :disabled="saving"
              />

            </div>


            <!-- STATUS - EDIT ONLY -->
            <div
              v-if="isEditMode"
              class="form-item status-form-item"
            >

              <label>
                Status
              </label>


              <div class="status-toggle-container">

                <label class="status-switch">

                  <input
                    v-model="isActive"
                    type="checkbox"
                    :true-value="1"
                    :false-value="0"
                    :disabled="saving"
                  />

                  <span class="status-slider"></span>

                </label>


                <span
                  class="status-text"
                  :class="{
                    'status-active': isActive == 1,
                    'status-inactive': isActive == 0
                  }"
                >
                  {{
                    isActive == 1
                      ? "Active"
                      : "Inactive"
                  }}
                </span>

              </div>

            </div>

          </div>


          <!-- CREATE INFO -->
          <div
            v-if="!isEditMode"
            class="create-info"
          >

            <i class="fas fa-info-circle"></i>

            <span>
              New system parameters will automatically be created as
              <strong>Active</strong>
              with
              <strong>Company ID 1</strong>.
            </span>

          </div>

        </div>


        <!-- ACTIONS -->
        <div class="step-navigation">

          <button
            type="button"
            class="btn btn-secondary"
            :disabled="saving"
            @click="cancel"
          >
            Cancel
          </button>


          <button
            type="button"
            class="btn button-default"
            :disabled="saving"
            @click="submitParameter"
          >

            <span
              v-if="saving"
              class="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>


            {{
              saving
                ? "Saving..."
                : isEditMode
                  ? "Update"
                  : "Save"
            }}

          </button>

        </div>

      </div>

    </div>

  </div>

</template>


<script>
import {
  createSystemParameter,
  viewSystemParameter,
  updateSystemParameter
} from "@/services/dataService";

import { useToast } from "vue-toastification";


export default {

  name: "AddSystemParameterForm",


  data() {

    return {

      token:
        localStorage.getItem("token_set") || "",

      loading: false,

      saving: false,

      parameterName: "",

      parameterValue: "",

      isActive: 1

    };

  },


  computed: {

    parameterId() {

      return this.$route.params.id || null;

    },


    isEditMode() {

      return !!this.parameterId;

    }

  },


  mounted() {

    if (this.isEditMode) {

      this.loadExistingParameter();

    }

  },


  methods: {

    /**
     * LOAD EXISTING PARAMETER FOR EDIT
     */
    async loadExistingParameter() {

      try {

        this.loading = true;


        const response =
          await viewSystemParameter(
            this.parameterId,
            this.token
          );


        if (
          response.data?.status ===
          "success"
        ) {

          const data =
            response.data?.data;


          if (!data) {

            this.toast.error(
              "System Parameter not found."
            );

            this.$router.push(
              "/system-parameters"
            );

            return;

          }


          this.parameterName =
            data.param_name || "";


          this.parameterValue =
            data.param_value ?? "";


          this.isActive =
            Number(
              data.is_active
            );

        } else {

          this.toast.error(
            response.data?.message ||
            "Failed to load System Parameter."
          );

        }

      } catch (error) {

        console.error(
          "Error loading System Parameter:",
          error
        );


        this.toast.error(
          error.response?.data?.message ||
          "Failed to load System Parameter."
        );


        this.$router.push(
          "/system-parameters"
        );

      } finally {

        this.loading = false;

      }

    },


    /**
     * VALIDATION
     */
    validateForm() {

      if (
        !this.parameterName ||
        !this.parameterName.trim()
      ) {

        this.toast.error(
          "Parameter Name is required."
        );

        return false;

      }


      if (
        this.parameterName.trim().length > 200
      ) {

        this.toast.error(
          "Parameter Name cannot exceed 200 characters."
        );

        return false;

      }


      if (
        this.parameterValue !== null &&
        String(
          this.parameterValue
        ).length > 200
      ) {

        this.toast.error(
          "Parameter Value cannot exceed 200 characters."
        );

        return false;

      }


      return true;

    },


    /**
     * CREATE OR UPDATE
     */
    async submitParameter() {

      if (this.saving) {
        return;
      }


      if (
        !this.validateForm()
      ) {

        return;

      }


      if (this.isEditMode) {

        await this.updateParameter();

      } else {

        await this.createParameter();

      }

    },


    /**
     * CREATE
     *
     * No is_active is sent.
     *
     * Backend automatically:
     *
     * company_id = 1
     * is_active = 1
     */
    async createParameter() {

      try {

        this.saving = true;


        const payload = {

          param_name:
            this.parameterName.trim(),

          param_value:
            this.parameterValue !== null
              ? String(
                  this.parameterValue
                ).trim()
              : null

        };


        const response =
          await createSystemParameter(
            payload,
            this.token
          );


        if (
          response.data?.status ===
          "success"
        ) {

          this.toast.success(
            response.data?.message ||
            "System Parameter created successfully."
          );


          this.$router.push(
            "/system-parameters"
          );

        } else {

          this.showApiError(
            response.data
          );

        }

      } catch (error) {

        console.error(
          "Error creating System Parameter:",
          error
        );


        this.showApiError(
          error.response?.data
        );

      } finally {

        this.saving = false;

      }

    },


    /**
     * UPDATE
     *
     * Edit can update:
     *
     * param_value
     * is_active
     */
    async updateParameter() {

      try {

        this.saving = true;


        const payload = {

          param_value:
            this.parameterValue !== null
              ? String(
                  this.parameterValue
                ).trim()
              : null,

          is_active:
            Number(
              this.isActive
            )

        };


        const response =
          await updateSystemParameter(
            this.parameterId,
            this.token,
            payload
          );


        if (
          response.data?.status ===
          "success"
        ) {

          this.toast.success(
            response.data?.message ||
            "System Parameter updated successfully."
          );


          this.$router.push(
            "/system-parameters"
          );

        } else {

          this.showApiError(
            response.data
          );

        }

      } catch (error) {

        console.error(
          "Error updating System Parameter:",
          error
        );


        this.showApiError(
          error.response?.data
        );

      } finally {

        this.saving = false;

      }

    },


    /**
     * API ERROR HANDLING
     */
    showApiError(data) {

      if (!data) {

        this.toast.error(
          "Something went wrong."
        );

        return;

      }


      if (
        data.data &&
        typeof data.data === "object"
      ) {

        const fields =
          Object.keys(
            data.data
          );


        if (
          fields.length > 0
        ) {

          const firstError =
            data.data[
              fields[0]
            ];


          if (
            Array.isArray(
              firstError
            ) &&
            firstError.length > 0
          ) {

            this.toast.error(
              firstError[0]
            );

            return;

          }

        }

      }


      this.toast.error(
        data.message ||
        "Something went wrong."
      );

    },


    /**
     * CANCEL
     */
    cancel() {

      this.$router.push(
        "/system-parameters"
      );

    }

  },


  setup() {

    const toast =
      useToast();


    return {
      toast
    };

  }

};
</script>


<style scoped lang="scss">

/*
|--------------------------------------------------------------------------
| FULL WIDTH RESPONSIVE CONTAINER
|--------------------------------------------------------------------------
*/

.system-parameter-form-container {
  width: 100%;
  max-width: 100%;
}

.card {
  width: 100%;
  max-width: 100%;
  margin: 0;
}

.form-sections {
  width: 100%;
  max-width: 100%;
  padding: 24px;
}


/*
|--------------------------------------------------------------------------
| SECTION
|--------------------------------------------------------------------------
*/

.info-section {
  width: 100%;
  max-width: 100%;
}

.section-title {
  width: 100%;

  font-size: 18px;
  font-weight: 600;

  margin-bottom: 24px;

  padding-bottom: 12px;

  border-bottom:
    1px solid #e5e7eb;
}


/*
|--------------------------------------------------------------------------
| DYNAMIC FORM GRID
|--------------------------------------------------------------------------
|
| Large screens:
| Parameter Name | Parameter Value
|
| Edit:
| Parameter Name | Parameter Value | Status
|
| Smaller screens automatically wrap.
|
*/

.form-grid {
  width: 100%;

  display: grid;

  grid-template-columns:
    repeat(
      auto-fit,
      minmax(280px, 1fr)
    );

  gap: 24px;
}


.form-item {
  width: 100%;

  display: flex;
  flex-direction: column;
}


.form-item label {
  font-size: 14px;
  font-weight: 600;

  margin-bottom: 8px;

  color: #333;
}


.form-item input {
  width: 100%;

  min-height: 42px;

  padding: 10px 12px;

  border:
    1px solid #ced4da;

  border-radius: 5px;

  font-size: 14px;

  background-color: #fff;

  transition:
    border-color 0.2s,
    box-shadow 0.2s;
}


.form-item input:focus {
  border-color: #007bff;

  box-shadow:
    0 0 0 2px
    rgba(0, 123, 255, 0.12);

  outline: none;
}


.form-item input:disabled {
  background-color: #e9ecef;

  color: #6c757d;

  cursor: not-allowed;
}


.req {
  color: red;

  margin-left: 3px;
}


.field-note {
  margin-top: 6px;

  color: #6c757d;

  font-size: 12px;
}


/*
|--------------------------------------------------------------------------
| STATUS TOGGLE
|--------------------------------------------------------------------------
*/

.status-form-item {
  justify-content: flex-start;
}


.status-toggle-container {
  min-height: 42px;

  display: flex;

  align-items: center;

  gap: 12px;
}


.status-switch {
  position: relative;

  display: inline-block;

  width: 52px;

  height: 28px;

  margin: 0 !important;
}


.status-switch input {
  opacity: 0;

  width: 0;

  height: 0;
}


.status-slider {
  position: absolute;

  cursor: pointer;

  inset: 0;

  background-color: #dc3545;

  border-radius: 30px;

  transition: 0.25s;
}


.status-slider::before {
  position: absolute;

  content: "";

  width: 20px;

  height: 20px;

  left: 4px;

  top: 4px;

  background-color: white;

  border-radius: 50%;

  transition: 0.25s;

  box-shadow:
    0 1px 3px
    rgba(0, 0, 0, 0.25);
}


.status-switch input:checked +
.status-slider {
  background-color: #069E27;
}


.status-switch input:checked +
.status-slider::before {
  transform: translateX(24px);
}


.status-switch input:disabled +
.status-slider {
  opacity: 0.6;

  cursor: not-allowed;
}


.status-text {
  font-size: 14px;

  font-weight: 600;
}


.status-active {
  color: #069E27;
}


.status-inactive {
  color: #dc3545;
}


/*
|--------------------------------------------------------------------------
| CREATE INFORMATION
|--------------------------------------------------------------------------
*/

.create-info {
  width: 100%;

  display: flex;

  align-items: center;

  gap: 8px;

  margin-top: 24px;

  padding: 12px 14px;

  border-radius: 5px;

  background-color: #eaf2ff;

  color: #1c66ad;

  font-size: 13px;
}


/*
|--------------------------------------------------------------------------
| ACTIONS
|--------------------------------------------------------------------------
*/

.step-navigation {
  width: 100%;

  display: flex;

  justify-content: flex-end;

  align-items: center;

  gap: 12px;

  margin-top: 30px;

  padding-top: 20px;

  border-top:
    1px solid #dee2e6;
}


.step-navigation .btn {
  min-width: 110px;
}


.button-default {
  color: #fff;

  background-color: #007bff;

  border-color: #007bff;
}


.button-default:hover {
  color: #fff;

  background-color: #0056b3;

  border-color: #0056b3;
}


/*
|--------------------------------------------------------------------------
| LOADING
|--------------------------------------------------------------------------
*/

.empty-state {
  width: 100%;

  text-align: center;

  padding: 50px 20px;

  color: #6c757d;
}


.empty-state i {
  font-size: 30px;

  margin-bottom: 10px;

  color: #007bff;
}


.empty-state p {
  margin-top: 10px;
}


/*
|--------------------------------------------------------------------------
| RESPONSIVE
|--------------------------------------------------------------------------
*/

@media (max-width: 768px) {

  .form-sections {
    padding: 16px;
  }


  .form-grid {
    grid-template-columns: 1fr;

    gap: 18px;
  }


  .step-navigation {
    flex-direction: column-reverse;

    align-items: stretch;
  }


  .step-navigation .btn {
    width: 100%;
  }

}
</style>